import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Box, CircularProgress, Alert } from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { invoiceService, ClientBillingData, ClientBillingPlacement } from '../services/invoiceService';
import { clientService } from '../services/clientService';
import { useAuth } from '../contexts/AuthContext';

const ClientBillingPage: React.FC = () => {
  const { isAgency } = useAuth();
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState<number | undefined>(undefined);
  const [monthFilter, setMonthFilter] = useState<number | undefined>(undefined);
  const [clientFilter, setClientFilter] = useState('');

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: isAgency,
  });
  const clients = clientsData?.data || [];

  const { data, isLoading, error } = useQuery({
    queryKey: ['client-billing', yearFilter, monthFilter, clientFilter, isAgency],
    queryFn: () => invoiceService.getMyBilling({
      year: yearFilter,
      month: monthFilter,
      clientId: clientFilter || undefined,
    }),
  });

  const billing = useMemo<ClientBillingData>(
    () => data?.data || { grandTotal: 0, totalPlacements: 0, monthlyTotals: [], placements: [] },
    [data]
  );

  const fmt = (amount: number | string | null | undefined) => {
    const n = typeof amount === 'number' ? amount : parseFloat(String(amount ?? 0));
    return `$${(isNaN(n) ? 0 : n).toFixed(2)}`;
  };

  const fmtDate = (value?: string | null) => {
    if (!value) return '—';
    try {
      return new Date(value).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const monthLabel = (m?: number) =>
    m ? new Date(2024, m - 1, 1).toLocaleString('default', { month: 'long' }) : '';

  const detailTableHead = ['Placement', 'Paid On', 'Order Month', 'Project', 'Type', 'Live URL', 'Amount'];

  const placementTableRow = (p: ClientBillingData['placements'][number]) => [
    p.placementId,
    fmtDate(p.paidAt || p.placedAt),
    fmtDate(p.orderDate),
    p.projectName || '',
    p.submissionType || 'GBS',
    p.liveUrl || '—',
    fmt(p.price),
  ];

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const selectedClientName = clientFilter
      ? clients.find((c) => c.id === clientFilter)?.name
      : undefined;

    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Billing Report', pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${reportDate}`, 14, 25);

    let yPos = 32;

    if (isAgency && selectedClientName) {
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text(`Client name: ${selectedClientName}`, 14, yPos);
      yPos += 9;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
    }

    let periodText = 'Period: ';
    periodText += monthFilter ? `${monthLabel(monthFilter)} ` : 'All months ';
    periodText += yearFilter ? `${yearFilter}` : 'All years';
    doc.text(periodText, 14, yPos);
    yPos += 12;

    const ensureSpace = (needed: number) => {
      if (yPos + needed > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
      }
    };

    const renderSummary = (label: string, total: number, count: number) => {
      ensureSpace(28);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(label, 14, yPos);
      yPos += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total Billing: ${fmt(total)}`, 14, yPos);
      yPos += 5;
      doc.text(`Total Paid Items: ${count}`, 14, yPos);
      yPos += 10;
    };

    const renderMonthlyTable = (
      monthlyTotals: ClientBillingData['monthlyTotals']
    ) => {
      if (monthlyTotals.length === 0) return;
      ensureSpace(24);
      autoTable(doc, {
        startY: yPos,
        head: [['Month', 'Year', 'Billing', 'Paid Items']],
        body: monthlyTotals.map((m) => [m.month, String(m.year), fmt(m.total), String(m.count)]),
        theme: 'grid',
        headStyles: { fillColor: [79, 70, 229], fontSize: 9 },
        bodyStyles: { fontSize: 8 },
        margin: { left: 14, right: 14 },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    };

    const renderPlacementTable = (placements: ClientBillingData['placements']) => {
      if (placements.length === 0) return;
      ensureSpace(24);
      autoTable(doc, {
        startY: yPos,
        head: [detailTableHead],
        body: placements.map(placementTableRow),
        theme: 'striped',
        headStyles: { fillColor: [79, 70, 229], fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 14, right: 14 },
      });
      yPos = (doc as any).lastAutoTable.finalY + 12;
    };

    if (isAgency && !clientFilter) {
      renderSummary('Overall Summary', billing.grandTotal, billing.totalPlacements);
      renderMonthlyTable(billing.monthlyTotals);

      const byClient = new Map<string, ClientBillingPlacement[]>();
      billing.placements.forEach((p) => {
        const key = p.clientName?.trim() || 'Unknown Client';
        const list = byClient.get(key) || [];
        list.push(p);
        byClient.set(key, list);
      });

      Array.from(byClient.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([clientName, clientPlacements]) => {
          const clientTotal = clientPlacements.reduce((sum, p) => sum + p.price, 0);

          ensureSpace(36);
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(79, 70, 229);
          doc.text(`Client name: ${clientName}`, 14, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 8;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(
            `Billing: ${fmt(clientTotal)} · ${clientPlacements.length} paid item${clientPlacements.length === 1 ? '' : 's'}`,
            14,
            yPos
          );
          yPos += 10;

          renderPlacementTable(clientPlacements);
        });
    } else {
      renderSummary('Summary', billing.grandTotal, billing.totalPlacements);
      renderMonthlyTable(billing.monthlyTotals);
      renderPlacementTable(billing.placements);
    }

    let filename = 'billing';
    if (isAgency && selectedClientName) {
      filename += `-${selectedClientName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
    } else if (isAgency) {
      filename += '-all-clients';
    }
    if (monthFilter) filename += `-${monthLabel(monthFilter).toLowerCase()}`;
    if (yearFilter) filename += `-${yearFilter}`;
    doc.save(`${filename}.pdf`);
  };

  return (
    <Box sx={{ p: 2.75, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Box sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.4px', color: '#111827' }}>
          Billing
        </Box>
        <Box sx={{ fontSize: '12.5px', color: '#6B7280', mt: 0.25 }}>
          {isAgency
            ? 'Stripe payments across your agency clients. Filter by client, month, or year.'
            : 'Your spend from completed Stripe payments (orders you paid for).'}
        </Box>
      </Box>

      {/* Filters + download */}
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,.05)',
          padding: '16px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
          {isAgency && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ fontSize: '11px', fontWeight: 600, color: '#6B7280' }}>Client name:</Box>
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                style={{ ...selectStyle, minWidth: 180 }}
              >
                <option value="">All clients</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </Box>
          )}
          <select
            value={monthFilter ?? ''}
            onChange={(e) => setMonthFilter(e.target.value ? Number(e.target.value) : undefined)}
            style={selectStyle}
          >
            <option value="">All months</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{monthLabel(m)}</option>
            ))}
          </select>
          <select
            value={yearFilter ?? ''}
            onChange={(e) => setYearFilter(e.target.value ? Number(e.target.value) : undefined)}
            style={selectStyle}
          >
            <option value="">All years</option>
            {Array.from({ length: 5 }, (_, i) => currentYear - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </Box>
        <Box
          component="button"
          onClick={handleDownloadPDF}
          disabled={billing.placements.length === 0}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: billing.placements.length === 0 ? '#C7D2FE' : '#4F46E5',
            color: '#fff',
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: billing.placements.length === 0 ? 'not-allowed' : 'pointer',
            boxShadow: '0 1px 2px rgba(79,70,229,0.25)',
            fontFamily: 'inherit',
            '&:hover': billing.placements.length === 0 ? {} : { background: '#4338CA' },
          }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download PDF
        </Box>
      </Box>

      {/* Summary cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
        {[
          {
            label: 'Total Billing',
            sub: 'From successful payments',
            value: fmt(billing.grandTotal),
            gradient: 'linear-gradient(135deg, #6D28D9 0%, #5B5BD6 40%, #4F46E5 100%)',
            icon: (
              <>
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </>
            ),
          },
          {
            label: 'Paid Items',
            sub: 'Sites paid via Stripe',
            value: String(billing.totalPlacements),
            gradient: 'linear-gradient(135deg, #DB2777 0%, #EC4899 50%, #F43F5E 100%)',
            icon: (
              <>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </>
            ),
          },
        ].map((card) => (
          <Box
            key={card.label}
            sx={{
              borderRadius: '12px',
              padding: '22px 24px',
              position: 'relative',
              overflow: 'hidden',
              background: card.gradient,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '12px' }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '9px', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {card.icon}
                </svg>
              </Box>
              <Box sx={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{card.label}</Box>
            </Box>
            <Box sx={{ fontSize: '30px', fontWeight: 700, color: '#fff', letterSpacing: '-0.8px', lineHeight: 1, fontFamily: 'JetBrains Mono, monospace' }}>
              {card.value}
            </Box>
            <Box sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', mt: '6px' }}>{card.sub}</Box>
          </Box>
        ))}
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">Failed to load billing. Please try again.</Alert>}

      {!isLoading && !error && billing.placements.length === 0 && (
        <Box
          sx={{
            background: '#FFFFFF',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6B7280',
          }}
        >
          <ReceiptLong sx={{ fontSize: 40, color: '#9CA3AF', mb: 1 }} />
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>No billing yet</Box>
          <Box sx={{ fontSize: '12.5px', mt: 0.5 }}>Charges appear here after a Stripe payment completes successfully.</Box>
        </Box>
      )}

      {!isLoading && !error && billing.placements.length > 0 && (
        <>
          {/* Monthly summary */}
          {billing.monthlyTotals.length > 0 && (
            <Box>
              <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1 }}>Monthly summary</Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {billing.monthlyTotals.map((m) => (
                  <Box
                    key={`${m.year}-${m.monthNum}`}
                    sx={{
                      background: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      minWidth: 160,
                      boxShadow: '0 1px 2px rgba(0,0,0,.05)',
                    }}
                  >
                    <Box sx={{ fontSize: '12px', color: '#6B7280', mb: '4px' }}>{m.month} {m.year}</Box>
                    <Box sx={{ fontSize: '17px', fontWeight: 700, color: '#4F46E5', fontFamily: 'JetBrains Mono, monospace' }}>{fmt(m.total)}</Box>
                    <Box sx={{ fontSize: '11.5px', color: '#9CA3AF', mt: '3px' }}>{m.count} paid item{m.count === 1 ? '' : 's'}</Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Placement details */}
          <Box>
            <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#111827', mb: 1 }}>Payment details</Box>
            <Box
              sx={{
                background: '#FFFFFF',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 2px rgba(0,0,0,.05)',
              }}
            >
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isAgency ? 860 : 760 }}>
                  <thead>
                    <tr>
                      {[
                        'Placement',
                        'Paid On',
                        'Order Month',
                        ...(isAgency ? ['Client name'] : []),
                        'Site URL',
                        'Project',
                        'Type',
                        'Live URL',
                        'Amount',
                      ].map((h, i, arr) => (
                        <th
                          key={h}
                          style={{
                            textAlign: i === arr.length - 1 ? 'right' : 'left',
                            fontSize: '10.5px',
                            fontWeight: 600,
                            color: '#9CA3AF',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '10px 14px',
                            borderBottom: '1px solid #E5E7EB',
                            background: '#FAFAFA',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {billing.placements.map((p) => (
                      <tr key={p.placementId}>
                        <td style={tdStyle}><span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11.5px', color: '#6B7280' }}>{p.placementId}</span></td>
                        <td style={tdStyle}>{fmtDate(p.paidAt || p.placedAt)}</td>
                        <td style={tdStyle}>{fmtDate(p.orderDate)}</td>
                        {isAgency && (
                          <td style={{ ...tdStyle, color: '#6B7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {p.clientName || '—'}
                          </td>
                        )}
                        <td style={tdStyle}>
                          <a href={p.siteUrl || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#4F46E5', textDecoration: 'none' }}>
                            {p.siteUrl}
                          </a>
                        </td>
                        <td style={{ ...tdStyle, color: '#6B7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.projectName}</td>
                        <td style={tdStyle}>
                          <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: '5px', fontSize: '11px', fontWeight: 700, background: '#4F46E5', color: '#fff' }}>
                            {p.submissionType || 'GBS'}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {p.liveUrl ? (
                            <a href={p.liveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#4F46E5', textDecoration: 'none' }}>View</a>
                          ) : (
                            <span style={{ color: '#9CA3AF' }}>—</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#111827' }}>{fmt(p.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={isAgency ? 8 : 7} style={{ ...tdStyle, fontWeight: 700, color: '#111827', borderTop: '2px solid #E5E7EB' }}>Total</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#4F46E5', fontSize: '13.5px', borderTop: '2px solid #E5E7EB' }}>{fmt(billing.grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const selectStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid #E5E7EB',
  borderRadius: '6px',
  padding: '7px 28px 7px 12px',
  fontFamily: 'inherit',
  fontSize: '13px',
  color: '#374151',
  background: '#FFFFFF',
  cursor: 'pointer',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: '12.5px',
  color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

export default ClientBillingPage;
